// ---------------------------------------------------------------------------
// EventHierarchyTree — 2-level collapsible tree: Block > Event
// ---------------------------------------------------------------------------

import { useState, useMemo, useCallback } from 'react'
import { ICON_MAP } from '@/lib/icons'
import { cn } from '@/lib/utils'
import { useStudioStore } from '@/store/studio-store'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { CreateFlowForm } from '@/features/flow-editor/components/create-flow-form'
import { buildEventHierarchy, roleToLabel, type EventHierarchyNode } from '@/features/flow-editor/services/event-hierarchy'
import { MoreHorizontal } from 'lucide-react'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { buildActionExecutionModel } from '@/features/flow-editor/services/actionflow-execution-model'
import { useStudioLocale } from '@/i18n'

const {
  ChevronDown,
  Zap,
  PlayCircle,
  Layers,
  AlertTriangle,
  Trash2,
} = ICON_MAP

// ---------------------------------------------------------------------------
// Event type badge colors
// ---------------------------------------------------------------------------

const EVENT_TYPE_COLORS: Record<string, string> = {
  tap: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  change: 'bg-orange-500/15 text-orange-600 dark:text-orange-400',
  submit: 'bg-primary/15 text-primary dark:text-primary',
  init: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
}

const DEFAULT_EVENT_COLOR = 'bg-muted text-muted-foreground'

// ---------------------------------------------------------------------------
// Status badge styles
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<string, string> = {
  spec: 'bg-primary/15 text-primary dark:text-primary',
  user: '',
  orphan: 'bg-destructive/15 text-destructive',
}

// ---------------------------------------------------------------------------
// Count events recursively
// ---------------------------------------------------------------------------

function countEvents(node: EventHierarchyNode): number {
  if (node.type === 'event') return 1
  return node.children.reduce((sum, child) => sum + countEvents(child), 0)
}

// Count bound flows recursively (unique flow names)
function countFlows(node: EventHierarchyNode): number {
  if (node.type === 'event') return node.flowName ? 1 : 0
  const flowNames = new Set<string>()
  function walk(n: EventHierarchyNode) {
    if (n.type === 'event' && n.flowName) flowNames.add(n.flowName)
    if (n.children) n.children.forEach(walk)
  }
  walk(node)
  return flowNames.size
}

// ---------------------------------------------------------------------------
// Create-flow affordance
// ---------------------------------------------------------------------------
//
// This file used to declare its OWN `CreateFlowForm` — same name as
// `flow-editor/components/create-flow-form.tsx`, same screen, different
// effect: it called `useFlowEditorStore.createFlow()` directly, so the flow
// existed in the editor store and NOWHERE else. It never reached
// `project.flows`, never queued persistence, never marked the project dirty,
// never appeared in the Flows list, and produced no receipt — the user's
// first and only signal was the duplicate-name error the NEXT time they typed
// the same name, for a flow they had never been shown (slice-5A §A2-a / D5).
//
// The two buttons are not semantically different: both say "create a flow" on
// one screen, and the census found no property the store-only one was meant
// to have. So there is one form now, and both affordances take the toolbar's
// write path (`mcpBus.execute('cogo.studio.addFlow')` → editor store +
// `project.flows` mirror + persistence + undo + toast). The gate lives in
// `__tests__/event-hierarchy-create-flow.test.tsx` and reads `project.flows`
// — the consumer — not whether a handler ran.

// ---------------------------------------------------------------------------
// EventHierarchyTree (exported)
// ---------------------------------------------------------------------------

export function EventHierarchyTree() {
  const { strings } = useStudioLocale()
  const project = useStudioStore((s) => s.currentProject)
  const flows = useFlowEditorStore((s) => s.flows)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showUnbound, setShowUnbound] = useState(false)
  const [showUnconnectedFlows, setShowUnconnectedFlows] = useState(false)

  const tree = useMemo(() => {
    if (!project?.manifest || !project?.blocks) return null
    return buildEventHierarchy(project.manifest, project.blocks)
  }, [project])

  const totalEvents = useMemo(() => (tree ? countEvents(tree) : 0), [tree])

  // Count unbound/orphan events
  const unboundCount = useMemo(() => {
    if (!tree) return 0
    let count = 0
    for (const block of tree.children) {
      for (const ev of block.children) {
        if ((!ev.flowName && !ev.actionName) || ev.status === 'orphan') count++
      }
    }
    return count
  }, [tree])

  // Filter tree by search query + hide unbound events
  const filteredTree = useMemo(() => {
    if (!tree) return tree
    const q = searchQuery.trim().toLowerCase()
    const filtered = {
      ...tree,
      children: tree.children
        .map((blockNode) => ({
          ...blockNode,
          children: blockNode.children.filter((ev) => {
            // Hide unbound/orphan events unless toggled
            if (!showUnbound && ((!ev.flowName && !ev.actionName) || ev.status === 'orphan')) return false
            // Search filter
            if (q) {
              return (
                (ev.flowName && ev.flowName.toLowerCase().includes(q)) ||
                (ev.actionName && ev.actionName.toLowerCase().includes(q)) ||
                (ev.elementId && ev.elementId.toLowerCase().includes(q)) ||
                (ev.eventType && ev.eventType.toLowerCase().includes(q)) ||
                (ev.label && ev.label.toLowerCase().includes(q))
              )
            }
            return true
          }),
        }))
        .filter((blockNode) => blockNode.children.length > 0),
    }
    return filtered
  }, [tree, searchQuery, showUnbound])

  const unboundFlows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const boundNames = new Set<string>()

    if (tree) {
      for (const block of tree.children) {
        for (const ev of block.children) {
          if (ev.flowName) boundNames.add(ev.flowName.toLowerCase())
        }
      }
    }

    const seen = new Set<string>()
    return flows.filter((flow) => {
      const name = flow.name.toLowerCase()
      if (seen.has(name)) return false
      const bound = Array.from(boundNames).some(
        (boundName) => name === boundName || name.endsWith(`.${boundName}`),
      )
      if (bound) return false
      seen.add(name)
      if (!q) return true
      return name.includes(q)
    })
  }, [flows, searchQuery, tree])

  const handleFlowBadgeClick = useCallback((flowName: string) => {
    const store = useFlowEditorStore.getState()
    // Match by bare name or full name
    const flow = store.flows.find(
      (f) =>
        f.name.toLowerCase() === flowName.toLowerCase() ||
        f.name.toLowerCase().endsWith(`.${flowName.toLowerCase()}`),
    )
    if (flow) {
      store.selectFlow(flow.id)
      store.setHighlightedFlowId(flow.id)
    }
  }, [])

  const handleChangeFlow = useCallback(
    (blockName: string, elementId: string, eventType: string, newFlowName: string) => {
      const store = useFlowEditorStore.getState()
      const flow = store.flows.find(
        (f) =>
          f.name.toLowerCase() === newFlowName.toLowerCase() ||
          f.name.toLowerCase().endsWith(`.${newFlowName.toLowerCase()}`),
      )
      if (flow) {
        store.bindFlowToEvent(flow.id, blockName, elementId, eventType)
      }
    },
    [],
  )

  const handleUnbindFlow = useCallback(
    (blockName: string, elementId: string, eventType: string) => {
      const store = useFlowEditorStore.getState()
      store.unbindFlowFromEvent(blockName, elementId, eventType)
    },
    [],
  )

  const handleUnboundEventClick = useCallback(
    (blockName: string, elementId: string, eventType: string) => {
      const store = useFlowEditorStore.getState()
      store.setSelectedEventContext({ blockName, elementId, eventType })
    },
    [],
  )

  if (!tree || tree.children.length === 0) {
    return (
      <div className="p-2 text-center text-xs text-muted-foreground">
        {strings.behavior.noBindings}
      </div>
    )
  }

  return (
    <div className="p-2">
      {/* Summary header */}
      <div className="flex items-center justify-between px-2 py-1 mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          Event Hierarchy ({totalEvents})
        </span>
        <button
          data-testid="flow-create-toggle"
          className="ml-auto text-[10px] px-2 py-0.5 rounded bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => setShowCreateForm((v) => !v)}
        >
          + Flow
        </button>
      </div>

      {/* Search filter */}
      {totalEvents > 8 && (
        <div className="px-2 mb-2">
          <input
            className="w-full text-[11px] border border-border rounded px-2 py-1 bg-background placeholder:text-muted-foreground/50"
            placeholder="Filter events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* Unbound events toggle */}
      {unboundCount > 0 && (
        <div className="px-2 mb-2">
          <button
            className={cn(
              'w-full text-[10px] px-2 py-1 rounded border transition-colors text-left',
              showUnbound
                ? 'border-muted-foreground/30 bg-muted/50 text-muted-foreground'
                : 'border-dashed border-muted-foreground/20 text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/30',
            )}
            onClick={() => setShowUnbound((v) => !v)}
          >
            {showUnbound ? strings.templateContent.hide : strings.templateContent.show} {unboundCount} unbound event{unboundCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* Inline create flow form — the SHARED one (see the note above). */}
      {showCreateForm && (
        <div className="mx-2 mb-2">
          <CreateFlowForm
            compact
            testIdPrefix="flow-create"
            onClose={() => setShowCreateForm(false)}
          />
        </div>
      )}

      {/* Block-level tree */}
      <div className="space-y-0.5">
        {(filteredTree ?? tree).children.map((blockNode) => (
          <BlockTreeNode
            key={blockNode.id}
            node={blockNode}
            onFlowClick={handleFlowBadgeClick}
            onUnboundEventClick={handleUnboundEventClick}
            onChangeFlow={handleChangeFlow}
            onUnbindFlow={handleUnbindFlow}
          />
        ))}
      </div>

      {unboundFlows.length > 0 && (
        <div className="mx-2 mt-3 rounded border border-dashed border-border bg-muted/10">
          <button
            type="button"
            data-testid="unconnected-flows-toggle"
            className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-[11px] text-muted-foreground hover:bg-muted/30"
            onClick={() => setShowUnconnectedFlows((value) => !value)}
            aria-expanded={showUnconnectedFlows}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <Layers className="h-3 w-3 shrink-0" />
              <span className="truncate font-medium text-foreground">
                {strings.behavior.flows} ({unboundFlows.length})
              </span>
            </span>
            <span className="shrink-0">{showUnconnectedFlows ? strings.templateContent.hide : strings.templateContent.show}</span>
          </button>
          {showUnconnectedFlows && (
            <div className="border-t border-border/70 p-2">
              <p className="mb-2 text-[11px] leading-snug text-muted-foreground">
                These flows are not necessarily wrong. Init, manual, shared, or draft flows can stay here; connect one only when a Page event should run it.
              </p>
              <div className="space-y-1">
                {unboundFlows.map((flow) => (
                  <div
                    key={flow.id}
                    data-testid="unconnected-flow-row"
                    className="flex w-full items-center gap-2 rounded border border-border bg-background px-2 py-1 text-left text-[11px] hover:bg-accent/50"
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 truncate text-left font-mono"
                      onClick={() => {
                        const store = useFlowEditorStore.getState()
                        store.selectFlow(flow.id)
                        store.setHighlightedFlowId(flow.id)
                      }}
                      title={`Open ${flow.name}`}
                    >
                      {flow.name}
                    </button>
                    <span className="shrink-0 text-muted-foreground">optional</span>
                    <button
                      type="button"
                      data-testid="unconnected-flow-delete"
                      className="shrink-0 rounded p-0.5 text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive transition-colors"
                      onClick={(event) => {
                        event.stopPropagation()
                        useFlowEditorStore.getState().deleteFlow(flow.id)
                      }}
                      title={`Delete flow ${flow.name}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// BlockTreeNode — Level 1 (Block, collapsible)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Group identical flow-pattern events within a block
// ---------------------------------------------------------------------------

interface EventGroup {
  kind: 'single' | 'grouped'
  events: EventHierarchyNode[]
  flowName: string
  actionName: string
  bindingKind: 'flow' | 'action'
  eventType: string
}

function groupIdenticalEvents(events: EventHierarchyNode[]): EventGroup[] {
  const groups: EventGroup[] = []
  const flowBuckets = new Map<string, EventHierarchyNode[]>()

  // Bucket events by binding kind + target + eventType.
  for (const ev of events) {
    const key = `${ev.bindingKind ?? 'flow'}::${ev.flowName ?? ''}::${ev.actionName ?? ''}::${ev.eventType ?? ''}`
    const bucket = flowBuckets.get(key)
    if (bucket) bucket.push(ev)
    else flowBuckets.set(key, [ev])
  }

  for (const [key, bucket] of flowBuckets) {
    const [bindingKind, flowName, actionName, eventType] = key.split('::')
    if (bucket.length >= 3 && (flowName || actionName)) {
      // Group 3+ identical-pattern events
      groups.push({
        kind: 'grouped',
        events: bucket,
        flowName,
        actionName,
        bindingKind: bindingKind as 'flow' | 'action',
        eventType,
      })
    } else {
      // Keep as individual items
      for (const ev of bucket) {
        groups.push({
          kind: 'single',
          events: [ev],
          flowName: ev.flowName ?? '',
          actionName: ev.actionName ?? '',
          bindingKind: ev.bindingKind ?? 'flow',
          eventType: ev.eventType ?? '',
        })
      }
    }
  }
  return groups
}

// ---------------------------------------------------------------------------
// GroupedEventRow — collapsed group of identical-pattern events
// ---------------------------------------------------------------------------

function GroupedEventRow({
  group,
  onFlowClick,
}: {
  group: EventGroup
  onFlowClick: (flowName: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const { events, flowName, actionName, bindingKind, eventType } = group
  const eventColor = EVENT_TYPE_COLORS[eventType] ?? DEFAULT_EVENT_COLOR

  return (
    <div className="rounded border border-border bg-muted/20">
      <div className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-[11px] hover:bg-accent/30 transition-colors">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <ChevronDown
            className={cn(
              'h-2.5 w-2.5 shrink-0 text-muted-foreground transition-transform',
              !expanded && '-rotate-90',
            )}
          />
          <span className={cn('text-[10px] px-1 py-0.5 rounded font-semibold uppercase', eventColor)}>
            {eventType}
          </span>
          <span className="font-mono text-[10px] text-muted-foreground/70">
            {events.length}x
          </span>
        </button>
        <span className="text-muted-foreground/40 text-[10px]">-&gt;</span>
        {bindingKind === 'flow' ? (
          <button
            type="button"
            className="min-w-0 truncate font-mono text-[11px] font-medium text-primary hover:underline"
            onClick={() => onFlowClick(flowName)}
            title={flowName}
          >
            {events[0]?.narrative?.effect ?? flowName}
          </button>
        ) : (
          <span className="min-w-0 truncate font-mono text-[11px] font-medium text-foreground" title={actionName}>
            {actionName} <span className="text-[9px] uppercase text-muted-foreground">direct</span>
          </span>
        )}
      </div>
      {expanded && (
        <div className="px-3 pb-1.5 space-y-0.5">
          {events.map((ev) => (
            <div key={ev.id} className="text-[10px] font-mono text-muted-foreground/70 py-0.5 flex items-center gap-1">
              <span className="text-muted-foreground/40">&bull;</span>
              {roleToLabel(ev.elementId ?? '')}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function BlockTreeNode({
  node,
  onFlowClick,
  onUnboundEventClick,
  onChangeFlow,
  onUnbindFlow,
}: {
  node: EventHierarchyNode
  onFlowClick: (flowName: string) => void
  onUnboundEventClick: (blockName: string, elementId: string, eventType: string) => void
  onChangeFlow: (blockName: string, elementId: string, eventType: string, newFlowName: string) => void
  onUnbindFlow: (blockName: string, elementId: string, eventType: string) => void
}) {
  const eventCount = useMemo(() => countEvents(node), [node])
  const flowCount = useMemo(() => countFlows(node), [node])
  // Collapse blocks with many events by default to reduce overwhelm
  const [isExpanded, setIsExpanded] = useState(eventCount <= 5)

  const eventGroups = useMemo(() => groupIdenticalEvents(node.children), [node.children])

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-2 py-1.5 text-xs font-medium hover:bg-accent/50 rounded-md">
        <ChevronDown
          className={cn(
            'h-3 w-3 shrink-0 text-muted-foreground transition-transform',
            !isExpanded && '-rotate-90',
          )}
        />
        <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate">{node.label}</span>
        <span className="ml-auto shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
          {eventCount} events{flowCount > 0 ? ` \u00b7 ${flowCount} flows` : ''}
        </span>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="ml-3 space-y-0.5">
          {eventGroups.map((group, idx) =>
            group.kind === 'grouped' ? (
              <GroupedEventRow
                key={`grp-${idx}`}
                group={group}
                onFlowClick={onFlowClick}
              />
            ) : (
              <EventLeafNode
                key={group.events[0].id}
                node={group.events[0]}
                onFlowClick={onFlowClick}
                onUnboundEventClick={onUnboundEventClick}
                onChangeFlow={onChangeFlow}
                onUnbindFlow={onUnbindFlow}
              />
            ),
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

// ---------------------------------------------------------------------------
// EventLeafNode — Level 2 (Event binding, flat leaf)
// ---------------------------------------------------------------------------

function EventLeafNode({
  node,
  onFlowClick,
  onUnboundEventClick,
  onChangeFlow,
  onUnbindFlow,
}: {
  node: EventHierarchyNode
  onFlowClick: (flowName: string) => void
  onUnboundEventClick: (blockName: string, elementId: string, eventType: string) => void
  onChangeFlow: (blockName: string, elementId: string, eventType: string, newFlowName: string) => void
  onUnbindFlow: (blockName: string, elementId: string, eventType: string) => void
}) {
  const flows = useFlowEditorStore((s) => s.flows)
  const eventType = node.eventType ?? 'tap'
  const flowName = node.flowName ?? ''
  const actionName = node.actionName ?? ''
  const targetName = flowName || actionName
  const isDirect = node.bindingKind === 'action' && Boolean(actionName)
  const directExecution = isDirect ? buildActionExecutionModel(actionName) : null
  const status = node.status ?? 'user'
  const triggerType = node.triggerType ?? 'event'
  const TriggerIcon = triggerType === 'init' ? PlayCircle : Zap
  const isUnbound = !targetName
  const isInit = triggerType === 'init'

  const eventColor = EVENT_TYPE_COLORS[eventType] ?? DEFAULT_EVENT_COLOR
  const statusStyle = STATUS_STYLES[status] ?? ''

  const handleClick = () => {
    if (isUnbound && node.blockName && node.elementId) {
      onUnboundEventClick(node.blockName, node.elementId, eventType)
    }
  }

  return (
    <div
      role={isUnbound ? 'button' : undefined}
      tabIndex={isUnbound ? 0 : undefined}
      onClick={isUnbound ? handleClick : undefined}
      onKeyDown={isUnbound ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() } : undefined}
      className={cn(
        'rounded border px-2 py-1.5 text-[11px]',
        // Init flows: purple left border
        isInit && 'border-l-2 border-l-purple-500',
        // Bound events with flow: subtle primary left border
        !isInit && targetName && 'border-l-2 border-l-primary/50',
        // Unbound events: dashed left border
        !isInit && isUnbound && 'border-l-2 border-dashed border-l-muted-foreground/30',
        status === 'spec'
          ? 'border-primary/20 bg-primary/5'
          : status === 'orphan'
            ? 'border-destructive/20 bg-destructive/5'
            : isUnbound
              ? 'border-dashed border-border bg-background cursor-pointer hover:bg-accent/50 transition-colors'
              : 'border-border bg-background',
      )}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        {status === 'orphan' ? (
          <AlertTriangle size={11} className="shrink-0 text-destructive" />
        ) : (
          <TriggerIcon
            size={11}
            className={cn(
              'shrink-0',
              status === 'spec' ? 'text-primary' : 'text-muted-foreground',
            )}
          />
        )}

        {/* Event type badge */}
        <span
          className={cn(
            'text-[10px] px-1 py-0.5 rounded font-semibold uppercase',
            eventColor,
          )}
        >
          {eventType}
        </span>

        {/* Element label */}
        {node.elementId && (
          <span className="font-mono text-muted-foreground text-[11px]">
            {roleToLabel(node.elementId)}
          </span>
        )}

        {/* Arrow + flow/direct action target + contextual unbind menu. */}
        {flowName ? (
          <>
            <span className="text-muted-foreground/40 text-[10px]">-&gt;</span>
            <button
              className="font-medium text-primary hover:underline text-[11px]"
              onClick={(e) => { e.stopPropagation(); onFlowClick(flowName) }}
              title={flowName}
            >
              {node.narrative?.effect ?? flowName}
            </button>
            {node.narrative?.effect && (
              <span className="font-mono text-[9px] text-muted-foreground/50">{flowName}</span>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="event-flow-menu"
                  className="ml-auto shrink-0 p-0.5 rounded text-muted-foreground/40 hover:text-muted-foreground hover:bg-accent/50 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  title="Change or unbind flow"
                >
                  <MoreHorizontal className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-1" align="end">
                {/* Change flow — list available flows */}
                {flows.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-accent/50 transition-colors flex items-center justify-between">
                        Change flow
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[180px] p-1" side="right" align="start">
                      {flows.map((f) => (
                        <button
                          key={f.id}
                          data-testid="event-flow-change-option"
                          className={cn(
                            'w-full text-left text-xs px-2 py-1 rounded hover:bg-accent/50 transition-colors font-mono',
                            f.name === flowName && 'bg-primary/10 text-primary',
                          )}
                          onClick={() => {
                            if (node.blockName && node.elementId) {
                              onChangeFlow(node.blockName, node.elementId, eventType, f.name)
                            }
                          }}
                        >
                          {f.name}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                )}
                <button
                  data-testid="event-flow-unbind"
                  className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors"
                  onClick={() => {
                    if (node.blockName && node.elementId) {
                      onUnbindFlow(node.blockName, node.elementId, eventType)
                    }
                  }}
                >
                  Unbind flow
                </button>
              </PopoverContent>
            </Popover>
          </>
        ) : isDirect ? (
          <>
            <span className="text-muted-foreground/40 text-[10px]">-&gt;</span>
            <span className="font-mono text-[11px] font-medium text-foreground">{actionName}</span>
            <span
              data-testid="event-direct-action-chip"
              className="rounded bg-sky-500/10 px-1 text-[8px] font-bold uppercase text-sky-700 dark:text-sky-300"
            >
              direct · {directExecution?.lane}
            </span>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="event-action-menu"
                  className="ml-auto shrink-0 rounded p-0.5 text-muted-foreground/40 transition-colors hover:bg-accent/50 hover:text-muted-foreground"
                  onClick={(event) => event.stopPropagation()}
                  title="Unbind direct action"
                >
                  <MoreHorizontal className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[180px] p-1" align="end">
                <button
                  data-testid="event-action-unbind"
                  className="w-full rounded px-2 py-1.5 text-left text-xs text-destructive transition-colors hover:bg-destructive/10"
                  onClick={() => {
                    if (node.blockName && node.elementId) {
                      onUnbindFlow(node.blockName, node.elementId, eventType)
                    }
                  }}
                >
                  Unbind direct action
                </button>
              </PopoverContent>
            </Popover>
          </>
        ) : (
          <span className="text-[10px] text-muted-foreground/50 italic">No binding</span>
        )}

        {/* Status badges */}
        {status === 'spec' && (
          <span className={cn('text-[8px] px-1 rounded uppercase font-bold', statusStyle)}>
            spec
          </span>
        )}
        {status === 'orphan' && (
          <span className={cn('text-[8px] px-1 rounded uppercase font-bold', statusStyle)}>
            orphan
          </span>
        )}
      </div>

      {/* Params */}
      {node.params && Object.keys(node.params).length > 0 && (
        <div className="ml-5 mt-1 space-y-0.5">
          {Object.entries(node.params).map(([key, val]) => (
            <div
              key={key}
              className="flex items-center gap-1 text-[10px] text-muted-foreground"
            >
              <span className="font-mono text-muted-foreground/70">{key}:</span>
              <span className="font-mono text-primary/70">{String(val)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
